/**
 * @file: useCreateOrder.ts
 * @copyright: (c) 2019-2021 sichuan zhichetech co., ltd.
 */

import type { AppNavParams } from '@euler/app/Routes';
import { useOrderContext } from '@euler/app/flows/order/functions/OrderContext';
import { orderFormFieldOf } from '@euler/app/flows/order/functions/orderFormFieldOf';
import { useCreateOrderRequest } from '@euler/app/flows/order/functions/useCreateOrderRequest';
import { useAppLoading } from '@euler/components/loading/Provider';
import { useAppReviewTracking } from '@euler/functions/useAppReviewTracking';
import { useConfirm } from '@euler/functions/ux/useConfirm';
import { markRequestViolation } from '@euler/lib/form/markRequestViolation';
import { duplicateConflictOf } from '@euler/lib/request/requestErrorDetails';
import { useServiceFactory } from '@euler/services/factory';
import { safeMarkDevFlag, showAlert, sleep } from '@euler/utils';
import { usePersistFn } from '@euler/utils/hooks';
import { useNavigation } from '@react-navigation/native';
import type { StackNavigationProp } from '@react-navigation/stack';
import { useOrderFlowContext } from '../../../OrderFlowContext';

const kSimulate = safeMarkDevFlag(false);
const kSimulateError = safeMarkDevFlag(false);
const kFake = safeMarkDevFlag(false);

export const useCreateOrder = () => {
  const trackAppReview = useAppReviewTracking();
  const appLoading = useAppLoading();
  const { orderService } = useServiceFactory();
  const { event$ } = useOrderContext();
  const confirm = useConfirm();
  const navigation = useNavigation<StackNavigationProp<AppNavParams>>();
  const {
    formController: { form },
    onCreated,
    finish,
  } = useOrderFlowContext();

  const getCreateOrderRequest = useCreateOrderRequest();

  return usePersistFn(
    async (args?: { shouldFinishPreInspection?: boolean }) => {
      try {
        const request = getCreateOrderRequest(args);
        __DEV__ && console.log(JSON.stringify(request, null, 2));

        appLoading.show();

        if (kFake) {
          await sleep(3000);
        } else {
          const { order, task } = await orderService.createOrderV2(request, {
            __fake__: kSimulate,
            __simulatefailure__: kSimulateError,
          });
          __DEV__ && console.log('place order success: ', order);
          finish({ simulate: kSimulate });

          event$.next({
            type: 'created',
            orderNo: order.orderNo,
            taskNo: task.taskNo,
            isMainOrder: true,
          });
          onCreated?.(order, task);

          trackAppReview();
        }
      } catch (e) {
        console.error(e);
        appLoading.hide();
        // The server's sentence is localized and names what was wrong (the
        // field, or the plate that already has an open order).
        const existing = duplicateConflictOf(e)?.existing;
        if (existing?.type === 'task') {
          const open = await confirm({
            title: '出错了',
            message: (e as Error).message,
            confirmButtonText: '查看',
          });
          if (open) navigation.push('Task', { taskNo: String(existing.id) });
          return;
        }
        // The marked input is on the form, one step back from this preview.
        if (markRequestViolation(form, e, orderFormFieldOf)) {
          navigation.goBack();
        }
        void showAlert('出错了', (e as Error).message);
      } finally {
        appLoading.hide();
      }
    },
  );
};
